import { RcTrans } from '@/components/atoms/RcTrans'
import { RcIconButton } from '@/components/molecules/interactive/RcIconButton'
import { useFeatures } from '@/components/providers/site/FeatureProvider'
import { useIsMobile } from '@/core/hooks/useMediaQueries'
import { useChatRoomDocument } from '@/entity/chat/useChatRoomDocument'
import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Form, Formik, FormikHelpers } from 'formik'
import { RefObject, useEffect, useRef, useState } from 'react'
import { useThrottledCallback } from 'use-debounce'
import { ArchivedChatView } from './ArchivedChatView'
import { handleOptionSelect, handleReactionSelect } from './chatInputHelpers'
import { ChatMentionsContainer } from './ChatMentionsContainer'
import { ReactionPicker } from './ReactionPicker'
import { TenorPicker } from './TenorSearch'

const MAX_CHAT_HEIGHT = 100
export const ChatInput = ({
  archived,
  chatId,
  autoFocus,
  inputRef: externalInputRef,
  existing,
  onSubmit,
  onEditPrevious,
  onCancel
}: {
  archived?: boolean
  chatId: string
  autoFocus?: boolean
  inputRef?: RefObject<HTMLInputElement>
  existing?: string
  onSubmit: (message: string) => any
  onEditPrevious?: () => any
  onCancel?: () => any
}) => {
  const isMobile = useIsMobile()
  const [expand, setExpand] = useState(false)
  const { featChatGifs } = useFeatures()
  const { members, typing } = useChatRoomDocument({ idOrKey: chatId })
  const containerRef = useRef<HTMLDivElement>(null)
  const internalInputRef = useRef<HTMLInputElement>(null)
  const initial = { message: existing || '' }
  const inputRef = externalInputRef || internalInputRef

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleTyping = useThrottledCallback(
    () => {
      typing()
    },
    2000,
    { leading: true, trailing: false }
  )

  const handleSubmit = async (
    values: typeof initial,
    helpers: FormikHelpers<typeof initial>
  ) => {
    helpers.resetForm()
    if (!values?.message?.trim()) return
    onSubmit(values.message.trim())
    inputRef.current?.focus?.()
  }

  useEffect(() => {
    autoFocus && inputRef.current?.focus()
  }, [autoFocus, inputRef])

  useEffect(() => {
    if (existing) {
      inputRef.current?.focus()
      const length = inputRef.current?.value.length || 0
      setTimeout(() => inputRef.current?.setSelectionRange(length, length))
    }
  }, [existing, inputRef])

  const uniqueAts = [
    ...members
      ?.filter(it => !!it.externalId)
      ?.map(it => ({
        id: it.id!,
        display: it.name,
        subtitle: it.image
      }))
  ]

  const validUniqueAts = uniqueAts.filter(option => option.display) as Array<{
    id: string
    display: string
    subtitle?: string
  }>

  if (archived) {
    return <ArchivedChatView />
  }

  const showControls = (values: typeof initial) =>
    featChatGifs && (isMobile || existing)
      ? expand || values.message.length < 2
      : true
  const showChevron = (values: typeof initial) =>
    featChatGifs && (isMobile || existing)
      ? !expand && values.message.length >= 2
      : false

  return (
    <Formik initialValues={initial} enableReinitialize onSubmit={handleSubmit}>
      {({
        values,
        setFieldValue,
        submitForm
      }: {
        values: typeof initial
        setFieldValue: (field: string, value: any) => void
        submitForm: () => void
      }) => (
        <Form id='chat-input-form'>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            mx={2}
            ref={containerRef}
          >
            <Fade in={showControls(values)}>
              {showControls(values) ? (
                <Stack direction={'row'}>
                  {featChatGifs ? (
                    <>
                      <TenorPicker
                        onSelected={val => {
                          handleReactionSelect(
                            val,
                            setFieldValue,
                            values,
                            inputRef
                          )
                        }}
                      />
                      <ReactionPicker
                        onSelected={value => {
                          handleReactionSelect(
                            value,
                            setFieldValue,
                            values,
                            inputRef
                          )
                        }}
                      />
                    </>
                  ) : null}
                  <RcIconButton
                    icon={['fal', 'at'] as const}
                    onClick={handleMenuOpen}
                  />
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                  >
                    {validUniqueAts.map(option => (
                      <MenuItem
                        key={option.id}
                        onClick={() =>
                          handleOptionSelect(
                            option,
                            setFieldValue,
                            values,
                            inputRef,
                            handleMenuClose
                          )
                        }
                      >
                        {option.display}
                      </MenuItem>
                    ))}
                  </Menu>
                </Stack>
              ) : (
                <Box />
              )}
            </Fade>
            <Fade in={showChevron(values)}>
              {showChevron(values) ? (
                <RcIconButton
                  icon={['fal', 'chevron-right']}
                  onClick={() => setExpand(true)}
                />
              ) : (
                <Box />
              )}
            </Fade>

            <ChatMentionsContainer
              values={values}
              setFieldValue={setFieldValue}
              submitForm={submitForm}
              onCancel={onCancel}
              onEditPrevious={onEditPrevious}
              handleTyping={handleTyping}
              inputRef={inputRef}
              validUniqueAts={validUniqueAts}
            />

            <RcIconButton
              disabled={!values?.message}
              icon={['fad', 'paper-plane']}
              onClick={submitForm}
              sx={theme => ({
                color: theme.palette.primary.main
              })}
            />
          </Stack>
          {existing ? (
            <Typography variant='subtitle2' color='text.secondary'>
              <RcTrans i18nKey='shared.chat-cancel-save'>
                escape to
                <a onClick={onCancel} href='#'>
                  cancel
                </a>
                • enter to
                <a onClick={submitForm} href='#'>
                  save
                </a>
              </RcTrans>
            </Typography>
          ) : null}
        </Form>
      )}
    </Formik>
  )
}
